import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import axios from "./axios";
import type { BillingCycle } from "./billing";
import { rememberCheckoutReference } from "./billing";

// --- Types -------------------------------------------------------------------

export type AvailabilityReason =
  | "email_taken"
  | "domain_taken"
  | "disposable_email"
  | "invalid_email";

export interface AvailabilityResult {
  available: boolean;
  reason: AvailabilityReason | null;
  /** Set only when reason === "domain_taken". */
  company_name: string | null;
}

export interface RegisterInput {
  email: string;
  password: string;
  password_confirmation: string;
  firstname: string;
  lastname: string;
  company_name: string;
  /** VAT RATE as a percentage (company.vat is decimal(10,2)), not a VAT number. */
  vat?: number | null;
  registration_id?: string | null;
  phone?: string | null;
  /**
   * Conditions Generales d'Utilisation et de Service.
   *
   * `boolean` et non `true` : le litteral rendait le refus inexprimable dans
   * le type, ce qui est exactement la garantie qu'il ne faut pas donner ici.
   * L'API valide `required|accepted` et repond 422 sur `false`.
   */
  accept_terms: boolean;
  /** Accord de sous-traitance (DPA, art. 28 RGPD). Distinct des CGU. */
  accept_dpa: boolean;
}

/** POST /auth/register returns the same payload as POST /user/login. */
export interface RegisterResult {
  id: number;
  email: string;
  /** SECURITY (VULN-02) : jeton de la session courante, a la racine. */
  token?: string;
  /** @deprecated L'API ne renvoie plus la liste des sessions. */
  sessions?: Array<{ token: string }>;
  company_users?: Array<{ company_id: number; company_role: string }>;
  [key: string]: any;
}

// --- Availability ------------------------------------------------------------

/**
 * Advisory check used by the signup form to fail fast on an email that cannot
 * open a new tenant. The server re-runs this inside POST /auth/register, so a
 * client that skips it (or races it) is still rejected correctly.
 */
export const useCheckAvailability = () =>
  useMutation({
    mutationFn: async (email: string): Promise<AvailabilityResult> => {
      const res = await axios.post("/auth/register/availability", { email });
      return res.data as AvailabilityResult;
    },
  });

/** Human-readable message for an availability failure. */
export const useAvailabilityMessage = () => {
  const { t } = useTranslation();
  return (result: AvailabilityResult | null): string | null => {
    if (!result || result.available) return null;
    switch (result.reason) {
      case "email_taken":
        return t(
          "register.errorEmailTaken",
          "An account already exists for this email. Sign in instead."
        );
      case "domain_taken":
        return result.company_name
          ? t("register.errorDomainTakenNamed", {
              defaultValue:
                "{{company}} is already on CodeNekt. Ask your fleet manager to invite you.",
              company: result.company_name,
            })
          : t(
              "register.errorDomainTaken",
              "Your company is already on CodeNekt. Ask your fleet manager to invite you."
            );
      case "disposable_email":
        return t("register.errorDisposableEmail", "Please use your work email address.");
      case "invalid_email":
        return t("required.emailInvalid", "Email is invalid");
      default:
        return t("common.errorGlobal", "An error occurred");
    }
  };
};

// --- Registration ------------------------------------------------------------

/**
 * Creates the company + fleet-manager account and signs the user in.
 *
 * The session is persisted here, in the same shape the login flow uses
 * (`token` + `currentUser`), because the very next step — creating the Stripe
 * Checkout session — is an authenticated call that reads the token from
 * localStorage via the axios interceptor.
 *
 * Note there is deliberately no page reload or redirect here: the wizard stays
 * mounted and moves straight on to payment. Reloading would drop the wizard
 * state and strand a user who has an account but no subscription.
 */
export const useRegister = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: RegisterInput): Promise<RegisterResult> => {
      const res = await axios.post("/auth/register", input);
      return res.data as RegisterResult;
    },
    onSuccess: (user) => {
      // SECURITY (VULN-02) : jeton a la racine (user.token), plus dans sessions[].
      const token = user?.token ?? user?.sessions?.[0]?.token;
      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("currentUser", JSON.stringify(user));
      }
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;

      if (status === 429) {
        toast.error(
          t("register.errorThrottled", "Too many attempts. Please try again later.")
        );
        return;
      }
      // 409 carries a specific, already-human message from the API
      // (email taken / domain taken) — surface it verbatim.
      toast.error(message || t("common.errorGlobal", "An error occurred"));
    },
  });
};

// --- Checkout for onboarding -------------------------------------------------

export interface OnboardingCheckoutInput {
  companyId: number;
  plan_slug: string;
  billing_cycle: BillingCycle;
  vehicle_quantity: number;
  ai_addon_slug?: string | null;
  /**
   * Only sent when the active provider expects the code from us
   * (capabilities.promo_collected_by === "app", i.e. Revolut).
   *
   * Stripe collects the code on its own hosted page, so sending it here as
   * well would let a customer apply one code in our form and a different one
   * on Stripe's — leaving the redemption record describing a code that was
   * never actually used.
   */
  promo_code?: string | null;
}

/**
 * Creates the checkout session for a brand-new company and returns the hosted
 * payment URL.
 *
 * Uses the existing authenticated billing endpoint — signup does not need a
 * second checkout implementation — but overrides the return URLs so the
 * provider comes back to the onboarding completion screen rather than /billing.
 * The API validates both URLs against an allowlist before using them.
 *
 * The {CHECKOUT_SESSION_ID} placeholder is Stripe-specific and is substituted
 * by Stripe. Revolut returns the URL verbatim, which is why the completion page
 * also reads a reference stashed in sessionStorage — see rememberCheckoutReference().
 */
export const useOnboardingCheckout = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: OnboardingCheckoutInput) => {
      const origin = window.location.origin;
      const { companyId, ...body } = input;

      const res = await axios.post(
        `/companies/${companyId}/billing/checkout-session`,
        {
          ...body,
          success_url: `${origin}/register/complete?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/register/plan?cancelled=1`,
        }
      );

      const session = res.data as { id: string; url: string };

      // Stashed before the redirect so the completion page can reconcile even
      // when the provider does not echo a reference back in the query string.
      if (session?.id) rememberCheckoutReference(session.id);

      return session;
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("register.errorCheckout", "Could not start the payment. Please try again.")
      );
    },
  });
};
